/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable no-param-reassign */
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Accordion,
  AccordionSummary,
  Avatar,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import * as Cesium from "cesium";
import FileCadIcon from "mdi-react/FileCadIcon";
import React from "react";
import { Choose, If } from "react-extras";
import { useTranslation } from "react-i18next";
import { useFileApi } from "~/api/GeneratedApi";
import { API_BASE_URL } from "~/config";
import useAuth from "~/utils/hooks/useAuth";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import {
  CesiumCadItem,
  CustomGeoJsonDataSource,
} from "../types/CesiumMapItems";
import { DrawerItem } from "./CesiumDrawerDataItems";

interface Props {
  item: DrawerItem<CesiumCadItem>;
  viewer: Cesium.Viewer;
  itemCount: number;
  itemAdded: () => void;
}

const loadGeoJson = async (
  url: string,
  viewer: Cesium.Viewer,
  tokenGetter: () => Promise<string | undefined>,
  itemCount: number,
  geoJsonId: number,
  designMaterialId: number,
): Promise<Cesium.GeoJsonDataSource | undefined> => {
  try {
    const token = await tokenGetter();
    if (!token) return undefined;
    const finalUrl = `${API_BASE_URL}${url}`;
    const resource = new Cesium.Resource({
      url: finalUrl,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      retryCallback: async (res?: Cesium.Resource) => {
        const tkn = await tokenGetter();
        if (!tkn || !res) return false;
        res.headers = {
          Authorization: `Bearer ${tkn}`,
        };
        return true;
      },
      retryAttempts: 1,
    });
    const src = await Cesium.GeoJsonDataSource.load(
      resource,
      getGeoJsonSettings(itemCount),
    );
    (src as CustomGeoJsonDataSource).layer = "cad";
    (src as CustomGeoJsonDataSource).geoJsonId = geoJsonId;
    setCadEntityProperties(src, designMaterialId);
    viewer.dataSources.add(src);
    return src;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

export const CesiumDrawerCadItem = ({
  item,
  viewer,
  itemCount,
  itemAdded,
}: Props): JSX.Element => {
  const { t } = useTranslation("cesium");
  const { getGeoJson } = useFileApi();
  const [loadId, setLoadId] = React.useState<number>();
  const cadSource = React.useRef<Cesium.GeoJsonDataSource>();
  const [isShowing, setIsShowing] = React.useState(false);
  const { getToken } = useAuth();
  const [loaded, setLoaded] = React.useState(false);

  const { isLoading: backendLoading } = useCustomQuery(
    { key: "geojson", id: loadId },
    () => getGeoJson(loadId!),
    {
      enabled: !!loadId,
      onSuccess: (d) => {
        loadGeoJson(
          d,
          viewer,
          getToken,
          itemCount,
          loadId!,
          item.mapItem.designMaterialId,
        ).then((src) => {
          if (!src) return;
          itemAdded();
          cadSource.current = src;
          setIsShowing(true);
          setLoaded(true);
        });
      },
    },
  );

  React.useEffect(() => {
    const { dataSources } = viewer;
    for (let i = 0; i < dataSources.length; i++) {
      const d = dataSources.get(i);
      if (
        d instanceof Cesium.GeoJsonDataSource &&
        (d as CustomGeoJsonDataSource).layer &&
        (d as CustomGeoJsonDataSource).geoJsonId === item.mapItem.id
      ) {
        setLoaded(true);
        setIsShowing(d.show);
        cadSource.current = d;
      }
    }
  }, [item.mapItem.id, viewer]);

  const handleVisibilityClick = (val: boolean) => {
    if (!cadSource.current) return;
    cadSource.current.show = val;
    setIsShowing(val);
  };

  const handleGoToClick = () => {
    if (!cadSource.current) return;
    viewer.zoomTo(cadSource.current);
  };

  const handleDownloadClick = () => {
    if (!loadId) setLoadId(item.mapItem.id);
  };

  const handleRemoveClick = () => {
    if (!cadSource.current) return;
    viewer.dataSources.remove(cadSource.current);
    cadSource.current = undefined;
    setLoaded(false);
    setLoadId(undefined);
  };

  const loading = React.useMemo(() => backendLoading, [backendLoading]);

  return (
    <Accordion
      expanded={false}
      elevation={1}
      TransitionProps={{ unmountOnExit: true }}
      disableGutters
      square
      sx={{
        backgroundColor: (theme) => theme.palette.primary.semiLight,
        color: (theme) => theme.palette.primary.contrastText,
        width: "100%",
      }}
    >
      <AccordionSummary sx={{ cursor: "default", alignItems: "center" }}>
        <Grid container justifyContent="center">
          <Grid
            item
            container
            xs
            flexDirection="row"
            alignItems="center"
            flexWrap="nowrap"
            sx={{ flexBasis: "100%" }}
          >
            <Grid item sx={{ mr: 1 }}>
              <Tooltip title={t("cad").toString()}>
                <Avatar
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.contrastText,
                  }}
                >
                  <FileCadIcon />
                </Avatar>
              </Tooltip>
            </Grid>
            <Grid item>
              <Typography sx={{ m: 1, wordBreak: "break-all" }}>
                {item.mapItem.name}
              </Typography>
            </Grid>
          </Grid>
          <If
            condition={loaded}
            render={() => (
              <>
                <Tooltip title={t("move-to").toString()}>
                  <Grid>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGoToClick();
                      }}
                      size="large"
                    >
                      <MyLocationIcon
                        sx={{
                          color: (theme) => theme.palette.primary.contrastText,
                        }}
                      />
                    </IconButton>
                  </Grid>
                </Tooltip>
                {isShowing ? (
                  <Tooltip title={t("hide").toString()}>
                    <Grid>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVisibilityClick(false);
                        }}
                        size="large"
                      >
                        <VisibilityOffIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.primary.contrastText,
                          }}
                        />
                      </IconButton>
                    </Grid>
                  </Tooltip>
                ) : (
                  <Tooltip title={t("show").toString()}>
                    <Grid>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVisibilityClick(true);
                        }}
                        size="large"
                      >
                        <VisibilityIcon
                          sx={{
                            color: (theme) =>
                              theme.palette.primary.contrastText,
                          }}
                        />
                      </IconButton>
                    </Grid>
                  </Tooltip>
                )}
              </>
            )}
          />
          <Choose>
            <Choose.When
              condition={!loading && !loaded}
              render={() => (
                <Tooltip title={t("download-data").toString()}>
                  <Grid>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadClick();
                      }}
                      size="large"
                    >
                      <GetAppIcon
                        sx={{
                          color: (theme) => theme.palette.primary.contrastText,
                        }}
                      />
                    </IconButton>
                  </Grid>
                </Tooltip>
              )}
            />
            <Choose.When
              condition={loading}
              render={() => (
                <Grid>
                  <CircularProgress
                    size={24}
                    sx={{
                      margin: (theme) => `auto ${theme.spacing(1)}`,
                      color: (theme) => theme.palette.primary.contrastText,
                    }}
                  />
                </Grid>
              )}
            />
            <Choose.When
              condition={!loading && loaded}
              render={() => (
                <Tooltip title={t("remove-from-map").toString()}>
                  <Grid>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveClick();
                      }}
                      size="large"
                    >
                      <CloseIcon
                        sx={{
                          color: (theme) => theme.palette.primary.contrastText,
                        }}
                      />
                    </IconButton>
                  </Grid>
                </Tooltip>
              )}
            />
          </Choose>
        </Grid>
      </AccordionSummary>
    </Accordion>
  );
};

export type CesiumGeoJsonColors = {
  fill: string;
  stroke: string;
  markerColor: string;
};
const CAD_COLORMAP: CesiumGeoJsonColors[] = [
  { fill: "#000", stroke: "#000", markerColor: "#000" },
  { fill: "#D0A310", stroke: "#000", markerColor: "#D0A310" },
  { fill: "#EF7748", stroke: "#000", markerColor: "#EF7748" },
  { fill: "#51bbed", stroke: "#000", markerColor: "#51bbed" },
];

function getGeoJsonSettings(
  itemCount: number,
): Cesium.GeoJsonDataSource.LoadOptions {
  const { length } = CAD_COLORMAP;
  const index = itemCount - Math.floor(itemCount / length) * length;
  const item = CAD_COLORMAP[index];
  const { fill, stroke, markerColor } = item;
  return {
    fill: Cesium.Color.fromCssColorString(fill).withAlpha(0.5),
    stroke: Cesium.Color.fromCssColorString(stroke),
    markerColor: Cesium.Color.fromCssColorString(markerColor).withAlpha(0.95),
    markerSymbol: "circle-stroked",
  };
}

function setCadEntityProperties(
  src: Cesium.GeoJsonDataSource,
  designMaterialId: number,
): void {
  src.entities.values.forEach((e) => {
    e.properties?.addProperty("type", "cad");
    e.properties?.addProperty("mapId", `cad${e.id}`);
    e.properties?.addProperty("designMaterialId", designMaterialId);
  });
}
